/* Reuse one server-filtered shelf. No product requests, content rewriting or purchase forms. */
(() => {
  'use strict';
  if (window.WKTArticleToyShelves) {
    window.WKTArticleToyShelves.init(document);
    return;
  }

  const BODY = '[data-wkt-article-body]';
  const OWNED = '[data-wkt-toy-owned]';
  const MANUAL = '.wkt-article-toys, .wkt-related-toys';
  const PROMO = `${MANUAL}, .wkt-verified-product, .wkt-product-card, .product-cta, .wkt-cta, .cta, .wkt-actions, a.button, a.wkt-btn, a.wkt-product-button, [data-wkt-promotion]`;
  const OMIT = `script, style, template, noscript, form, button, input, select, textarea, nav, [role="navigation"], .toc, .wkt-toc, .table-of-contents, [id="toc"], [id="table-of-contents"], [data-toc], [hidden], [aria-hidden="true"], ${PROMO}`;
  const ATOMIC = 'ul, ol, dl, table, figure, figcaption, blockquote, details, summary, form, button, iframe, video, audio, canvas, svg, pre, aside, [role="tablist"], [role="tabpanel"], .wkt-card-grid, .wkt-grid, .wkt-card, .wkt-figure, .wkt-fig, .cards, .grid, .card, .wkt-hero, .hero, .lead, .planning-card, [data-wkt-no-shelf]';
  const BLOCK = /^(ADDRESS|ARTICLE|ASIDE|BLOCKQUOTE|DIV|DL|DT|DD|FIGCAPTION|FIGURE|FOOTER|H[1-6]|HEADER|LI|MAIN|NAV|OL|P|PRE|SECTION|TABLE|TD|TH|TR|UL|DETAILS|SUMMARY)$/;
  const HEADING = /^H[1-6]$/;
  const initialized = new WeakSet();

  const requestedCount = words => words > 2000 ? 3 : words > 1000 ? 2 : words > 500 ? 1 : 0;
  const countWords = text => (text.trim().match(/\S+/gu) || []).filter(word => /[\p{L}\p{N}]/u.test(word)).length;

  function analyze(body) {
    const strings = new WeakMap();
    const hiddenNodes = new WeakMap();
    const skipped = new WeakMap();
    const candidates = [];
    const promotions = [];
    let cursor = 0;

    function hidden(node) {
      if (!hiddenNodes.has(node)) {
        const style = getComputedStyle(node);
        hiddenNodes.set(node, node.matches('[hidden], [aria-hidden="true"]') || style.display === 'none' || style.visibility === 'hidden');
      }
      return hiddenNodes.get(node);
    }

    function excluded(node) {
      if (node.nodeType !== 1) return false;
      if (!skipped.has(node)) skipped.set(node, node.matches(OMIT) || hidden(node));
      return skipped.get(node);
    }

    function textOf(node) {
      if (node.nodeType === 3) return node.textContent;
      if (node.nodeType !== 1 || excluded(node)) return ' ';
      if (strings.has(node)) return strings.get(node);
      const text = node.tagName === 'BR' ? ' ' : Array.from(node.childNodes).map(textOf).join('');
      const result = BLOCK.test(node.tagName) ? ` ${text} ` : text;
      strings.set(node, result);
      return result;
    }
    const wordsOf = node => countWords(textOf(node));

    // Promotion spacing is independent of insertion traversal: an atomic grid,
    // FAQ, list or card can contain a CTA even though it must never be split.
    function collectPromotions(container, start, insidePromotion = false) {
      let position = start;
      const closedDetails = container.tagName === 'DETAILS' && !container.hasAttribute('open');
      const visibleSummary = closedDetails && Array.from(container.children).find(child => child.tagName === 'SUMMARY');
      for (const node of container.childNodes) {
        // Closed details still contribute editorial FAQ words, but their hidden
        // promotions cannot provide a visible end shelf or block nearby shelves.
        if (closedDetails && node !== visibleSummary) { if (!insidePromotion) position += wordsOf(node); continue; }
        if (node.nodeType === 3) { if (!insidePromotion) position += wordsOf(node); continue; }
        if (node.nodeType !== 1 || hidden(node)) continue;
        const promotion = node.matches(PROMO) && !(node.tagName === 'DETAILS' && !node.hasAttribute('open'));
        if (promotion) promotions.push({ node, words: position });
        else if (excluded(node)) continue;
        collectPromotions(node, position, insidePromotion || promotion);
        if (!insidePromotion) position += wordsOf(node);
      }
    }

    function editorialWordsAfter(node) {
      let words = 0;
      for (let current = node; current && current !== body; current = current.parentElement) {
        for (let sibling = current.nextSibling; sibling; sibling = sibling.nextSibling) words += wordsOf(sibling);
      }
      return words;
    }

    const faqHeading = node => /^(frequently asked questions|common(?: [\p{L}\p{N}-]+){0,4} questions|questions and answers|faqs?)\b/iu.test(node.textContent.trim());

    function isFaq(node) {
      if (node.matches('[itemtype*="FAQPage"]')) return true;
      if (/(^|[-_\s])(faq|faqs|accordion)([-_\s]|$)/i.test(`${node.id} ${node.className}`)) return true;
      const heading = Array.from(node.children).find(child => HEADING.test(child.tagName));
      if (heading) return faqHeading(heading);
      return Array.from(node.children).some(child => child.matches('details, [itemtype*="FAQPage"]'));
    }

    function canWalk(node) {
      if (node.matches(ATOMIC) || isFaq(node)) return false;
      if (!/^(DIV|SECTION|ARTICLE|MAIN)$/.test(node.tagName)) return false;
      const display = getComputedStyle(node).display;
      return display !== 'grid' && display !== 'inline-grid' && display !== 'flex' && display !== 'inline-flex';
    }

    function nextReadable(node) {
      let next = node.nextSibling;
      while (next && (next.nodeType === 8 || (next.nodeType === 3 && !next.textContent.trim()) || (next.nodeType === 1 && !next.matches(PROMO) && wordsOf(next) === 0))) next = next.nextSibling;
      return next;
    }

    function standaloneHeading(node) {
      if (HEADING.test(node.tagName)) return node;
      const heading = node.querySelector('h1, h2, h3, h4, h5, h6');
      return heading && wordsOf(node) === wordsOf(heading) ? heading : null;
    }

    function visit(container, depth) {
      let faqLevel = null;
      for (const node of container.childNodes) {
        if (node.nodeType === 3) { cursor += wordsOf(node); continue; }
        if (node.nodeType !== 1) continue;
        if (node.matches(PROMO)) continue;
        if (excluded(node)) continue;
        const heading = standaloneHeading(node);
        if (heading) {
          const level = Number(heading.tagName.slice(1));
          if (faqHeading(heading)) faqLevel = level;
          else if (faqLevel !== null && level <= faqLevel) faqLevel = null;
        }
        const start = cursor;
        if (faqLevel === null && canWalk(node)) visit(node, depth + (node.tagName === 'SECTION' ? 1 : 0));
        else cursor += wordsOf(node);
        const next = nextReadable(node);
        // Never separate a heading from its text, a lead-in from its list/table,
        // or place the shelf inside an atomic component. Whole sections stay intact
        // whenever a similarly positioned outer boundary is available.
        if (faqLevel === null && cursor > start && next && !heading && !(next.nodeType === 1 && next.matches(PROMO))) {
          const nextAtomic = next.nodeType === 1 && next.matches('ul, ol, dl, table, figure, details, blockquote');
          const leadIn = /[:：]\s*$/.test(node.textContent.trim());
          if (!nextAtomic && !leadIn) candidates.push({ node, words: cursor, depth });
        }
      }
    }
    collectPromotions(body, 0);
    visit(body, 0);
    const words = wordsOf(body);
    const edge = Math.max(100, words * 0.12);
    const unique = new Map();
    for (const candidate of candidates) {
      if (candidate.words < edge || candidate.words > words - edge) continue;
      if (promotions.some(promotion => Math.abs(candidate.words - promotion.words) < 120)) continue;
      const previous = unique.get(candidate.words);
      if (!previous || candidate.depth < previous.depth || (candidate.depth === previous.depth && previous.node.parentElement !== candidate.node.parentElement)) unique.set(candidate.words, candidate);
    }
    const hasManualEndShelf = promotions.some(promotion => promotion.node.matches(MANUAL) && editorialWordsAfter(promotion.node) === 0);
    return { words, candidates: Array.from(unique.values()).sort((a, b) => a.words - b.words), promotions, hasManualEndShelf };
  }

  function placements(analysis, wanted) {
    const { words, candidates } = analysis;
    const gap = Math.max(150, words * 0.18);
    // At most three placements: ordered dynamic programming keeps natural spacing
    // without a greedy first choice preventing a later safe placement.
    for (let count = wanted; count > 0; count--) {
      const rows = [];
      for (let slot = 0; slot < count; slot++) {
        const target = words * (slot + 1) / (count + 1);
        rows[slot] = candidates.map((candidate, index) => {
          const cost = Math.abs(candidate.words - target) + candidate.depth * words * 0.035;
          if (!slot) return { cost, indices: [index] };
          let best = null;
          for (let previous = 0; previous < index; previous++) {
            const state = rows[slot - 1][previous];
            if (!state || candidate.words - candidates[previous].words < gap) continue;
            if (!best || state.cost + cost < best.cost) best = { cost: state.cost + cost, indices: [...state.indices, index] };
          }
          return best;
        });
      }
      const best = rows[count - 1].filter(Boolean).sort((a, b) => a.cost - b.cost)[0];
      if (best) return best.indices.map(index => candidates[index]);
    }
    return [];
  }

  function copyShelf(source, key, suffix) {
    const clone = source.cloneNode(true);
    clone.removeAttribute('data-wkt-toy-source');
    clone.removeAttribute('data-wkt-toy-source-for');
    const prefix = `WktToys-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}-${suffix}`;
    const ids = new Map();
    const elements = [clone, ...clone.querySelectorAll('*')];
    elements.filter(element => element.id).forEach((element, index) => {
      const id = `${prefix}-${index}`;
      ids.set(element.id, id);
      element.id = id;
    });
    for (const element of elements) {
      for (const attribute of ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns', 'for', 'headers']) {
        if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
      }
      const href = element.getAttribute('href');
      if (href && href.startsWith('#') && ids.has(href.slice(1))) element.setAttribute('href', `#${ids.get(href.slice(1))}`);
    }
    return clone;
  }

  function initialize(body) {
    if (initialized.has(body)) return;
    initialized.add(body);
    body.querySelectorAll(OWNED).forEach(shelf => shelf.remove());
    const key = body.dataset.wktToyKey;
    const sourceContainer = Array.from(body.parentElement.querySelectorAll('[data-wkt-toy-source-for]')).find(element => element.dataset.wktToySourceFor === key);
    let source = sourceContainer && (sourceContainer.tagName === 'TEMPLATE' ? sourceContainer.content.querySelector('[data-wkt-toy-source]') : sourceContainer);
    const analysis = analyze(body);
    const wanted = requestedCount(analysis.words);
    body.dataset.wktEditorialWords = String(analysis.words);
    body.dataset.wktShelvesRequested = String(wanted);
    body.dataset.wktShelvesPlaced = '0';
    if (!source || !source.querySelector('.wkt-article-toys__card')) {
      body.dataset.wktShelvesReason = 'no-eligible-products';
      return;
    }
    // Only a visible authored shelf with no editorial content after it replaces
    // the end shelf. A mid-body shelf or a CSS marker cannot suppress the ending.
    if (sourceContainer.tagName === 'TEMPLATE' && !analysis.hasManualEndShelf) {
      source = copyShelf(source, key, 'end');
      source.dataset.wktToySource = '';
      source.dataset.wktToySourceFor = key;
      sourceContainer.replaceWith(source);
    }
    body.dataset.wktEndShelf = analysis.hasManualEndShelf ? 'authored' : 'dynamic';
    const selected = placements(analysis, wanted);
    selected.forEach((placement, index) => {
      const shelf = copyShelf(source, key, `body-${index + 1}`);
      shelf.dataset.wktToyOwned = key;
      shelf.dataset.wktToyPlacement = 'inline';
      shelf.dataset.wktAfterWords = String(placement.words);
      placement.node.after(shelf);
    });
    body.dataset.wktShelvesPlaced = String(selected.length);
    body.dataset.wktShelvesReason = selected.length === wanted ? 'complete' : 'safe-spacing-limit';
  }

  function init(root) {
    if (root.matches && root.matches(BODY)) initialize(root);
    root.querySelectorAll(BODY).forEach(initialize);
  }
  window.WKTArticleToyShelves = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', () => init(document));
})();
